/**
 * PlanningLadder: where a planning run stands, as one short row of rungs.
 *
 * A planning run speaks only at its milestone beats, minutes apart, so the
 * card used to sit on "Drafting the plan" with nothing to show that the run
 * had already read the repository or what was still to come (2026-10-06).
 * The ladder names every step once, marks the ones behind the run, and counts
 * the time on the one it is on. It is read from the same beats the
 * conversation prints, and from nothing else: a step the run never reported
 * is never drawn as done.
 *
 * Milestones are matched by name, not through the shared type, so a beat this
 * bundle does not know yet (or one the shared build has not caught up with)
 * is ignored rather than breaking the row.
 */
import type { ReactNode } from "react";
import type { CodingEvent, WorkItemRunListEntry } from "@yagni/shared";
import { sinceLabel } from "./LiveStream.js";

export type LadderRungId = "read" | "draft" | "angles" | "check" | "write" | "last_read" | "preview" | "ready";

/** `stopped` is the rung an ended run was on when it ended without a plan. */
export type LadderRungState = "done" | "now" | "pending" | "skipped" | "stopped";

export interface LadderRung {
  id: LadderRungId;
  word: string;
  state: LadderRungState;
  /** The small word under the rung: elapsed time, a call count, "reused". Null for none. */
  sub: string | null;
  /** Set on the read rung when the run reused an earlier run's reading. */
  reused?: boolean;
}

const RUNGS: ReadonlyArray<{ id: LadderRungId; word: string }> = [
  { id: "read", word: "Read" },
  { id: "draft", word: "Draft" },
  { id: "angles", word: "Angles" },
  { id: "check", word: "Check" },
  { id: "write", word: "Write" },
  { id: "last_read", word: "Last read" },
  // Drawn only for a plan that gets a UI preview: its gate waits for it.
  { id: "preview", word: "Preview" },
  { id: "ready", word: "Ready" },
];

/** Which rung a beat puts the run on. Absent means the beat moves nothing. */
const MILESTONE_RUNG: Record<string, LadderRungId> = {
  reading_reused: "read",
  drafting: "draft",
  drafting_unread: "draft",
  second_look: "read",
  angles: "angles",
  checking: "check",
  writing: "write",
  last_read: "last_read",
  revising: "last_read",
  prototype: "preview",
  plan_ready: "ready",
};

function milestoneOf(event: CodingEvent): string | null {
  if (event.kind !== "message") return null;
  const milestone = (event as { milestone?: unknown }).milestone;
  return typeof milestone === "string" ? milestone : null;
}

function indexOf(id: LadderRungId): number {
  return RUNGS.findIndex((rung) => rung.id === id);
}

/**
 * The ladder as of `nowMs`. A rung is done only when a beat reached it (the
 * sequential fallback skips Angles and Write, and the ladder must not draw
 * them done); the rung the run is on now counts its time; a second look at
 * the repository puts it back on Read without un-doing the draft it already
 * made. Once the run has ended (`live` false) nothing is "now": the plan is
 * ready and every rung is done, or the run ended on a rung and that rung
 * says stopped.
 */
export function planningLadderOf(events: readonly CodingEvent[], nowMs: number, live = true): LadderRung[] {
  let toolCalls = 0;
  let anyMilestone = false;
  let reused = false;
  let unread = false;
  let ready = false;
  let revising = false;
  let current: number | null = null;
  let currentAt = 0;
  const reached = new Set<number>();

  for (const event of events) {
    if (event.kind === "tool_call") {
      toolCalls += 1;
      continue;
    }
    const milestone = milestoneOf(event);
    if (!milestone) continue;
    const rungId = MILESTONE_RUNG[milestone];
    // A beat with no rung (the sketch's "attached") moves nothing.
    if (!rungId) continue;
    anyMilestone = true;
    const index = indexOf(rungId);
    reached.add(index);
    if (milestone === "reading_reused") {
      // The reading happened in an earlier run; nothing is running on Read now.
      reused = true;
      current = null;
      continue;
    }
    if (milestone === "drafting_unread") unread = true;
    if (milestone === "plan_ready") {
      ready = true;
      current = null;
      continue;
    }
    revising = milestone === "revising";
    current = index;
    currentAt = event.at;
  }

  const readIndex = indexOf("read");
  // Before the first beat, a run making tool calls is reading the repository.
  const readingNow = live && !anyMilestone && toolCalls > 0;
  // The rung an ended run was on when it ended. A finished plan is "ready",
  // so a current rung on an ended run means the run stopped there.
  const endedOn = !live && !ready ? current : null;
  if (!live) current = null;
  // A plan with no preview never reaches the Preview rung, so it is not drawn.
  const sketched = reached.has(indexOf("preview"));

  return RUNGS.map((rung, index): LadderRung => {
    const base = { id: rung.id, word: rung.word };
    if (index === readIndex) {
      if (readingNow) return { ...base, state: "now", sub: `${toolCalls} call${toolCalls === 1 ? "" : "s"}` };
      if (current === readIndex) {
        return { ...base, state: "now", sub: sinceLabel(currentAt, nowMs), ...(reused ? { reused } : {}) };
      }
      if (endedOn === readIndex) return { ...base, state: "stopped", sub: "stopped here" };
      if (reused) return { ...base, state: "done", sub: "reused", reused: true };
      if (unread) return { ...base, state: "skipped", sub: "no repo" };
      // Any later beat means the reading, cached or not, is behind the run.
      return { ...base, state: anyMilestone || (!live && toolCalls > 0) ? "done" : "pending", sub: null };
    }
    if (ready) return { ...base, state: "done", sub: null };
    if (index === current) {
      const since = sinceLabel(currentAt, nowMs);
      return { ...base, state: "now", sub: revising ? `revising · ${since}` : since };
    }
    if (index === endedOn) return { ...base, state: "stopped", sub: "stopped here" };
    return { ...base, state: reached.has(index) ? "done" : "pending", sub: null };
  }).filter((rung) => rung.id !== "preview" || sketched);
}

export interface PlanningLadderProps {
  /** The run on the card. The ladder draws only for a planning run. */
  run: { purpose?: WorkItemRunListEntry["purpose"] | null } | null;
  events: readonly CodingEvent[];
  /** A live run with no beats yet still shows the empty ladder. */
  live: boolean;
  /** The clock the current rung counts against: now while live, the end once it stopped. */
  nowMs: number;
}

export function PlanningLadder({ run, events, live, nowMs }: PlanningLadderProps): ReactNode {
  if (run?.purpose !== "planning") return null;
  // An ended run that never reported a beat (a run from before the beats
  // existed) has no ladder to draw; a live one shows the empty ladder.
  if (!live && !events.some((event) => milestoneOf(event) != null && MILESTONE_RUNG[milestoneOf(event) ?? ""])) {
    return null;
  }
  const rungs = planningLadderOf(events, nowMs, live);
  return (
    <ol className="ls-ladder" data-testid="planning-ladder" aria-label="Planning steps">
      {rungs.map((rung) => (
        <li
          key={rung.id}
          className={`ls-rung is-${rung.state}`}
          data-rung={rung.id}
          data-state={rung.state}
          aria-current={rung.state === "now" ? "step" : undefined}
        >
          <span className="ls-rung-dot" aria-hidden />
          <span className="ls-rung-word">{rung.word}</span>
          {rung.sub && <span className="ls-rung-sub">{rung.sub}</span>}
        </li>
      ))}
    </ol>
  );
}
